import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { AnalyticsService } from "./analytics.service";

@Injectable()
export class LeasingService {
  constructor(private http: HttpClientService, private analyticsService: AnalyticsService) {}

  public getLeasing({
    id,
    residual_value = 0,
    insurance_plan = "contado",
    insurance_amount = 0,
    new_asset = undefined,
    term = undefined,
    year = new Date().getFullYear().toString(),
    car_use = "personal",
    forced_profile = undefined,
    price = 0,
    downpayment = 0,
    locator = false,
    modelo_id = undefined,
    income = undefined,
    expenses = undefined,
    locator_monthly_rent = undefined,
    locator_installation_fee = undefined,
    opening_percentage = undefined,
    deposit_percentage = undefined,
    siting_state_id = undefined,
    old = undefined,
    product = undefined,
    financed_accessories = undefined,
    demo = undefined,
    apply_subsidy = undefined,
    application_id = undefined,
    version_id = undefined,
    includes_vehicle_tax = undefined,
    assurant_gap_amount = undefined,
    assurant_sra_amount = undefined,
    frae_amount = undefined,
  }): Observable<any> {
    const options = {
      params: {
        downpayment: String(downpayment),
        term: term,
        price: String(price),
        auto_tipo_uso: car_use,
        year: year,
        residual_value: String(residual_value),
        insurance_plan: insurance_plan,
        insurance_amount: String(insurance_amount),
        new_asset: new_asset,
        locator: (locator == null ? true : locator).toString(),
        forced_profile: forced_profile,
        modelo_id: modelo_id,
        ingresos_comprobables: income,
        expenses: expenses,
        locator_monthly_rent: locator_monthly_rent,
        locator_installation_fee: locator_installation_fee,
        opening_percentage: opening_percentage,
        deposit_percentage: deposit_percentage,
        siting_state_id: siting_state_id,
        old: old,
        product: product,
        financed_accessories: financed_accessories,
        demo: demo,
        apply_subsidy: apply_subsidy,
        application_id: application_id,
        version_id: version_id,
        includes_vehicle_tax: includes_vehicle_tax !== undefined ? String(includes_vehicle_tax) : undefined,
        assurant_gap_amount: assurant_gap_amount != null ? String(assurant_gap_amount) : undefined,
        assurant_sra_amount: assurant_sra_amount != null ? String(assurant_sra_amount) : undefined,
        frae_amount: frae_amount != null ? String(frae_amount) : undefined,
      },
    };
    return this.http
      .get("/api/persons/" + id + "/leasings/leasing", options)
      .pipe(tap((_) => console.log("LeasingService.getLeasing: Ok")));
  }

  public getAllInsuranceUnits(car_year, model_id, is_new): Observable<any> {
    const options = {
      params: {
        car_year: car_year,
        model_id: model_id,
        new_or_used: is_new ? "auto_nuevo" : "auto_usado",
      },
    };
    return this.http
      .get("/api/insurance/vehicles", options)
      .pipe(tap((_) => console.log("LeasingService.getAllInsuranceUnits: Ok")));
  }

  public notifyNoVehicle(
    brand: string,
    year: string,
    model: string,
    version: string,
    broker: string,
    is_new: boolean
  ): Observable<any> {
    const params = {
      brand: brand,
      year: year,
      model: model,
      version: version,
      broker: broker,
      is_new: is_new,
    };

    return this.http
      .post("/api/insurance/notify_no_vehicle", params)
      .pipe(tap((_) => console.log("LeasingService.notifyNoVehicle: Ok")));
  }

  public notifyNoInsurance(
    brand: string,
    year: string,
    model: string,
    version: string,
    is_new: boolean
  ): Observable<any> {
    const params = {
      brand: brand,
      year: year,
      model: model,
      version: version,
      is_new: is_new,
    };

    return this.http
      .post("/api/insurance/notify_no_insurance", params)
      .pipe(tap((_) => console.log("LeasingService.notifyNoInsurance: Ok")));
  }

  public putQuote(id, quote): Observable<any> {
    return this.http.put("/api/quotes/" + id, quote).pipe(
      tap((_) => {
        console.log("LeasingService.putQuote: Ok.");
        this.analyticsService.quoteRequestUpdatedEvent(id, quote.product);
      })
    );
  }

  public addQuote(id, quote): Observable<any> {
    Object.assign(quote, { url_origen: "/crm", auto_tipo_uso: "personal", tipo: "dealer" });
    return this.http.post("/api/persons/" + id + "/quotes/single_quote", quote).pipe(
      tap((_) => {
        console.log("LeasingService.addQuote: Ok.");
        this.analyticsService.quoteRequestCreatedEvent(id, quote.product);
      })
    );
  }

  public leasingSimulation(
    lease_options,
    gps_options,
    insurance_options,
    residual_options,
    car_price,
    state_vehicle_tax
  ): Observable<any> {
    const options = {
      loan: lease_options.loan,
      interest_rate: lease_options.interest_rate,
      term: lease_options.term,
      lease_contributions: lease_options.contributions,
      lease_term_contributions: lease_options.term_contributions,
      gps_rent: gps_options.rent,
      insurance_is_financed: insurance_options.is_financed,
      insurance_plan: insurance_options.is_financed ? "financiado" : "contado",
      insurance_amount: insurance_options.amount,
      insurance_contributions: insurance_options.contributions,
      security_deposit_amount: residual_options.security_deposit,
      deposit_pct: residual_options.deposit_percentage,
      residual_amount: residual_options.amount,
      residual_contributions: residual_options.contributions,
      car_price: car_price,
      financed_accessories: lease_options.financed_accessories,
      downpayment: lease_options.downpayment,
      state_vehicle_tax: state_vehicle_tax,
    };
    return this.http
      .post("/api/amortization/leasing_simulation", options)
      .pipe(tap((_) => console.log("LeasingService.leasingSimulation: Ok")));
  }

  public leasingSimulationPDF(
    lease_options,
    gps_options,
    state_vehicle_tax,
    insurance_options,
    residual_options,
    car_price,
    columns,
    downpayment,
    person_id,
    model_id,
    assurant_gap_monthly = 0,
    assurant_sra_monthly = 0,
    frae_monthly = 0
  ): Observable<any> {
    const options = {
      loan: lease_options.loan,
      interest_rate: lease_options.interest_rate,
      term: lease_options.term,
      lease_contributions: lease_options.contributions,
      lease_term_contributions: lease_options.term_contributions,
      gps_rent: gps_options.rent,
      insurance_is_financed: insurance_options.is_financed,
      insurance_plan: insurance_options.is_financed ? "financiado" : "contado",
      insurance_amount: insurance_options.amount,
      insurance_contributions: insurance_options.contributions,
      security_deposit_amount: residual_options.security_deposit,
      residual_amount: residual_options.amount,
      residual_contributions: residual_options.contributions,
      price: car_price,
      columns: columns,
      downpayment: downpayment,
      person_id: person_id,
      model_id: model_id,
      state_vehicle_tax: state_vehicle_tax,
      assurant_gap_monthly: assurant_gap_monthly,
      assurant_sra_monthly: assurant_sra_monthly,
      frae_monthly: frae_monthly,
    };
    return this.http
      .post("/api/amortization/leasing_simulation_pdf", options, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("LeasingService.leasingSimulationPDF: Ok.")));
  }

  public printTaxTableV2(payload): Observable<any> {
    return this.http
      .post("/api/amortization/print_tax_table_v2", payload, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("LeasingService.printTaxTableV2: Ok.")));
  }

  public getRainbow({
    id,
    residual_value = 0,
    insurance_plan = "contado",
    insurance_amount = 0,
    tipo_prestamo = undefined,
    term = undefined,
    year = new Date().getFullYear().toString(),
    car_use = "personal",
    profile = undefined,
    price = 0,
    downpayment = 0,
    gps = false,
    modelo_id = undefined,
    income = undefined,
    expenses = undefined,
    rainbow_bucket = undefined,
    rainbow_profile = undefined,
  }): Observable<any> {
    const options = {
      params: {
        downpayment: String(downpayment),
        term: term,
        price: String(price),
        auto_tipo_uso: car_use,
        year: String(year),
        residual_value: String(residual_value),
        insurance_plan: insurance_plan,
        insurance_amount: String(insurance_amount),
        tipo_prestamo: tipo_prestamo,
        localizador: String(gps),
        profile: profile,
        modelo_id: modelo_id,
        ingresos_comprobables: income,
        expenses: expenses,
        rainbow_bucket: rainbow_bucket,
        rainbow_profile: rainbow_profile,
      },
    };
    return this.http
      .get("/api/persons/" + id + "/leasings/rainbow", options)
      .pipe(tap((_) => console.log("LeasingService.getRainbow: Ok")));
  }

  public preauth_letter({ id, price, downpayment, modelo_id, product, insurance_amount }): Observable<any> {
    const options = {
      params: {
        downpayment: String(downpayment),
        price: String(price),
        modelo_id: modelo_id,
        product: product,
        insurance_amount: String(insurance_amount),
      },
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/quotes/" + id + "/preauth_letter", options)
      .pipe(tap((_) => console.log("LeasingService.preauth_letter: Ok.")));
  }

  public auth_letter(id): Observable<any> {
    const options = {
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/applications/" + id + "/auth_letter", options)
      .pipe(tap((_) => console.log("LeasingService.auth_letter: Ok.")));
  }

  public subsidy_letter(id): Observable<any> {
    const options = {
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/applications/" + id + "/subsidy_letter", options)
      .pipe(tap((_) => console.log("LeasingService.subsidy_letter: Ok.")));
  }

  public subsidy_no_future_deduction_letter(id): Observable<any> {
    const options = {
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/applications/" + id + "/subsidy_no_future_deduction_letter", options)
      .pipe(tap((_) => console.log("LeasingService.subsidy_no_future_deduction_letter: Ok.")));
  }

  public purchase_order_letter(id): Observable<any> {
    const options = {
      responseType: "blob" as "json",
    };
    return this.http
      .get("/api/applications/" + id + "/purchase_order_letter", options)
      .pipe(tap((_) => console.log("LeasingService.purchase_order_letter: Ok.")));
  }
}
